/**
 * FirebaseMicrosoftAdapter - Firebase Authentication with Microsoft Provider
 *
 * This adapter implements authentication via Firebase using Microsoft
 * (Entra ID) as the OAuth provider. This is the primary authentication
 * method for enterprise users.
 *
 * Migration Note (2026-02-22):
 * This replaces the EntraAdapter per Architecture Guardrail 1-006.
 * Firebase handles token management; the backend API sets custom claims.
 */

import { type User, onAuthStateChanged, type Unsubscribe } from 'firebase/auth';
import {
  auth,
  signInWithMicrosoft,
  signOut,
  getUserClaims,
  callSetClaimsEndpoint,
  refreshUserToken,
  UserNotAuthorizedError,
  AuthBackendUnavailableError,
} from '../firebase';
import type {
  AuthAdapter,
  AuthUser,
  AuthProviderType,
  LoginOptions,
} from './AuthAdapter';
import { AuthError } from './AuthAdapter';

/**
 * FirebaseMicrosoftAdapter
 *
 * Implements the AuthAdapter interface using Firebase Authentication
 * with the Microsoft OAuth provider.
 */
export class FirebaseMicrosoftAdapter implements AuthAdapter {
  readonly providerType: AuthProviderType = 'firebase-microsoft';

  private currentUser: User | null = null;
  private authStateUnsubscribe: Unsubscribe | null = null;

  constructor() {
    // Subscribe to auth state changes
    this.authStateUnsubscribe = onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
    });
  }

  /**
   * Clean up auth state listener
   */
  dispose(): void {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
      this.authStateUnsubscribe = null;
    }
  }

  /**
   * Sign in with Microsoft via popup
   */
  async login(_options?: LoginOptions): Promise<void> {
    try {
      const user = await signInWithMicrosoft();
      this.currentUser = user;

      // Always call backend to set/refresh claims and link external_id
      // The backend is idempotent - it will link external_id on first call
      // and just refresh claims on subsequent calls
      await callSetClaimsEndpoint(user);
      // Force token refresh to get the latest claims
      await refreshUserToken(user);
    } catch (error) {
      console.error('[FirebaseMicrosoftAdapter] Login error:', error);
      
      // If user is not authorized, sign them out and throw specific error
      if (error instanceof UserNotAuthorizedError) {
        await signOut();
        this.currentUser = null;
        throw new AuthError(
          error.message,
          'USER_NOT_AUTHORIZED',
          this.providerType,
          error
        );
      }

      if (error instanceof AuthBackendUnavailableError) {
        throw new AuthError(
          error.message,
          'BACKEND_UNAVAILABLE',
          this.providerType,
          error
        );
      }
      
      throw new AuthError(
        'Failed to sign in with Microsoft',
        'LOGIN_FAILED',
        this.providerType,
        error
      );
    }
  }

  /**
   * Sign out the current user
   */
  async logout(): Promise<void> {
    try {
      await signOut();
      this.currentUser = null;
    } catch (error) {
      console.error('[FirebaseMicrosoftAdapter] Logout error:', error);
      throw new AuthError(
        'Failed to sign out',
        'LOGOUT_FAILED',
        this.providerType,
        error
      );
    }
  }

  /**
   * Get the current authenticated user
   */
  async getUser(): Promise<AuthUser | null> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      const idToken = await user.getIdToken();

      return {
        id: user.uid,
        email: user.email || '',
        displayName: user.displayName || user.email || 'User',
        photoUrl: user.photoURL || undefined,
        idToken,
        // Access token not needed - Firebase handles token validation
        accessToken: idToken,
        tokenExpiry: undefined, // Firebase handles expiry
      };
    } catch (error) {
      console.error('[FirebaseMicrosoftAdapter] getUser error:', error);
      return null;
    }
  }

  /**
   * Get roles from Firebase custom claims
   */
  async getRoles(): Promise<string[]> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return [];
    }

    try {
      const claims = await getUserClaims(user);
      // Return single role as array for compatibility
      return claims.role ? [claims.role] : [];
    } catch (error) {
      console.error('[FirebaseMicrosoftAdapter] getRoles error:', error);
      return [];
    }
  }

  /**
   * Check if a user is currently authenticated
   */
  isAuthenticated(): boolean {
    return !!(this.currentUser ?? auth.currentUser);
  }

  /**
   * Silently acquire a fresh token
   */
  async acquireTokenSilent(): Promise<string | null> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      // Force refresh to get a new token
      const token = await user.getIdToken(true);
      return token;
    } catch (error) {
      console.error('[FirebaseMicrosoftAdapter] acquireTokenSilent error:', error);
      return null;
    }
  }

  /**
   * Handle redirect callback (not needed for popup flow)
   */
  async handleRedirectCallback(): Promise<void> {
    // Firebase popup flow handles this automatically
    // No action needed
  }
}

/**
 * Singleton adapter instance
 */
export const firebaseMicrosoftAdapter = new FirebaseMicrosoftAdapter();
